import { useEffect, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { Share2, Eye } from "lucide-react"
import { getProfile } from "~/api/auth"
import type { ProfileResponse } from "~/api/auth"
import { markShareCelebrationSeen } from "~/api/portrait"
import { HeroPortraitFrame } from "~/components/HeroPortraitFrame"
import { PageShell } from "~/components/PageShell"
import { ShareableCardModal } from "~/components/ShareableCardModal"
import { useShareCardSession } from "~/hooks/useShareCardSession"
import { heroPortraitUrl } from "~/utils/heroPortraitUrl"

export function PortraitCelebrationPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getProfile()
      .then(async (data) => {
        setProfile(data)
        const dog = data.dog_profile
        if (!dog || dog.portrait.status !== "ready" || dog.share_celebration_seen_at) {
          const emailVerified = searchParams.get("email_verified") === "1" ? "1" : undefined
          navigate(emailVerified ? "/dashboard?email_verified=1" : "/dashboard", { replace: true })
          return
        }
        try {
          await markShareCelebrationSeen()
        } catch {
          // Still show celebration; dashboard panel remains available
        }
      })
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setLoading(false))
  }, [navigate, searchParams])

  const dog = profile?.dog_profile
  const {
    session,
    modalOpen,
    openShareModal,
    closeShareModal,
    personalizedPortraitUrl,
    canShare,
  } = useShareCardSession({
    assessmentSessionToken: dog?.assessment_session_token,
    archetypeIcon: dog?.archetype_icon,
    portrait: dog?.portrait,
    enabled: Boolean(dog),
  })

  if (loading || !dog) {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  const dogName = dog.name
  const archetypeLabel = dog.archetype_label ?? "Archetype"
  const heroImage = heroPortraitUrl(dog.archetype_icon, dog.portrait)
  const emailVerified = searchParams.get("email_verified") === "1" ? "1" : undefined
  const dashboardPath = emailVerified ? "/dashboard?email_verified=1" : "/dashboard"

  return (
    <PageShell innerClassName="max-w-lg w-full">
      <div
        data-testid="portrait-celebration"
        className="flex w-full flex-col items-center text-center"
      >
        <p className="text-sm font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">
          Portrait ready
        </p>
        <h1 className="mt-2 text-3xl font-semibold text-[var(--color-text-primary)]">
          {dogName}&apos;s personality is ready to share
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-[var(--color-text-secondary)]">
          Celebrate {dogName}, {archetypeLabel}. Share their personality with friends and family.
        </p>

        {heroImage && (
          <HeroPortraitFrame className="mt-6" src={heroImage} alt={`${dogName} portrait`} />
        )}

        <button
          type="button"
          onClick={openShareModal}
          disabled={!canShare}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--color-text-primary)] px-6 py-3 font-medium text-[var(--color-surface-card)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Share2 size={18} aria-hidden />
          Share {dogName}&apos;s personality
        </button>

        <button
          type="button"
          onClick={openShareModal}
          disabled={!canShare}
          className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-[var(--color-text-primary)] underline disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Eye size={15} aria-hidden />
          Preview share card
        </button>

        <Link
          to={dashboardPath}
          className="mt-6 text-sm font-medium text-[var(--color-text-secondary)] underline"
        >
          Continue to dashboard
        </Link>
      </div>

      {session && (
        <ShareableCardModal
          isOpen={modalOpen}
          onClose={closeShareModal}
          data={session}
          personalizedPortraitUrl={personalizedPortraitUrl}
        />
      )}
    </PageShell>
  )
}
